"use client";

import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import * as React from "react";

import { useBackCloses } from "@/components/ui/back-closes";
import { Button } from "@/components/ui/button";
import { DrawerGrabber, useDrawerDrag } from "@/components/ui/drawer-grabber";
import { useScopeClass } from "@/components/ui/scope";
import { keyboardDrawerStyle, useVirtualKeyboardInset } from "@/components/ui/virtual-keyboard";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useReloadHold } from "@/lib/reload-guard";
import { isEmbeddedInApp } from "@/lib/native-bridge";
import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";

/** Whether the enclosing `Dialog` may be dismissed by accident (see `dismissible`). */
const DialogDismissibleContext = React.createContext(true);

/** Close reasons that are "the member didn't mean it" rather than an explicit close. */
const ACCIDENTAL_CLOSE_REASONS: ReadonlySet<string> = new Set([
  "outside-press",
  "escape-key",
  "focus-out",
  "close-watcher",
]);

function Dialog({
  backCloses,
  dismissible = true,
  actionsRef: outerActionsRef,
  onOpenChange,
  disablePointerDismissal,
  ...props
}: DialogPrimitive.Root.Props & {
  /** Browser Back closes this dialog (see `back-closes.tsx`). Undefined = inherit from
   *  `<BackClosesOverlays>`; pass `false` for a dialog whose open state lives in the URL. */
  backCloses?: boolean;
  /**
   * `false`: only an explicit close (the X, a Cancel/Close button, browser Back where
   * `backCloses` applies) dismisses it — a backdrop tap, Escape and the mobile drawer's
   * drag-down are ignored. For flow-critical popups the member must not lose by a stray
   * tap: the composer's authwall, a code-entry step (2026-10-23). Default `true` keeps
   * every other dialog as it was.
   */
  dismissible?: boolean;
}) {
  const ownActionsRef = React.useRef<DialogPrimitive.Root.Actions | null>(null);
  const actionsRef = outerActionsRef ?? ownActionsRef;
  // Base UI's imperative close runs the caller's onOpenChange(false) like any other dismissal.
  // Back stays wired on a non-dismissible dialog on purpose: without the pushed entry,
  // Back would leave the PAGE (the composer) rather than just this popup.
  useBackCloses(backCloses, props.open === true && !!onOpenChange, () =>
    actionsRef.current?.close(),
  );
  // An open dialog is work in progress — never auto-reload for a new build under it.
  useReloadHold(props.open === true);
  const handleOpenChange: DialogPrimitive.Root.Props["onOpenChange"] = dismissible
    ? onOpenChange
    : (open, details) => {
        if (!open && ACCIDENTAL_CLOSE_REASONS.has(details.reason)) {
          details.cancel();
          return;
        }
        onOpenChange?.(open, details);
      };
  return (
    <DialogDismissibleContext.Provider value={dismissible}>
      <DialogPrimitive.Root
        data-slot="dialog"
        actionsRef={actionsRef}
        disablePointerDismissal={disablePointerDismissal ?? !dismissible}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DialogDismissibleContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        // Fade via Base UI's transition model (start/ending-style) so the exit holds
        // opacity 0 and doesn't snap back to grey for a frame — mirrors the Sheet
        // overlay. The keyframe classes stay for the enter feel.
        "fixed inset-0 isolate z-50 bg-black/10 duration-150 transition-opacity data-starting-style:opacity-0 data-ending-style:opacity-0 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  mobileDrawer,
  mobileCloseButton = false,
  largeCloseButton = false,
  overlayClassName,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean;
  /**
   * Force the corner close X on the mobile drawer even when `showCloseButton={false}`
   * (a mobile-only X). By default a bottom drawer ALREADY shows the corner X on mobile
   * (alongside the grabber) whenever `showCloseButton` is true, so this is only needed
   * to add a mobile X to an otherwise X-less (`showCloseButton={false}`) drawer.
   */
  mobileCloseButton?: boolean;
  /**
   * On small screens, dock to the bottom and slide up like a drawer (centered dialog above
   * the breakpoint). `"sm"` docks below 767px; `true`/`"md"` docks below 769px — the
   * mobile↔desktop boundary (≤768 = mobile/iOS-style layout).
   *
   * DEFAULT: customer-facing (non-admin) dialogs dock to the bottom below md (`"md"` —
   * ≤768) so the whole site treats <769 as the compact tier; admin dialogs
   * stay centered. Pass an explicit value to override — e.g. `false` for a full-screen
   * takeover, or `"sm"` to keep a dialog centered from 767 up.
   */
  mobileDrawer?: boolean | "sm" | "md";
  /** Render a bigger close button (larger tap target) — used on the public site. */
  largeCloseButton?: boolean;
  overlayClassName?: string;
}) {
  // Re-apply the scope class (e.g. `admin-scope`) here so scope-based overrides
  // reach the popup, which the portal renders outside the scoped DOM subtree.
  const scopeClass = useScopeClass();
  // Public-site dialogs dim without blurring, matching the old MUI backdrop
  // (rgba(0,0,0,0.5)); admin keeps the lighter bg-black/10 overlay default.
  // Callers can still override per-dialog via overlayClassName (twMerge wins).
  const isAdmin = scopeClass === "admin-scope";
  // Site popups all dock to the bottom below md (769px — ≤768 is the mobile tier) by
  // default, so the whole customer site treats <769 as the compact/mobile tier (the
  // desktop boundary the header + catalog use). admin stays centered. An explicit
  // `mobileDrawer` (incl. `false`) always wins. Per-dialog `sm:max-w-[…]` widths were
  // migrated to `md:max-w-[…]` so the custom desktop width lands at the md boundary.
  const drawer = mobileDrawer ?? (isAdmin ? false : "md");

  // Drag-to-dismiss for the bottom-drawer variants — the shared DrawerGrabber owns
  // the gesture (mobile only; hidden at the centered breakpoint, so drag never
  // fires on desktop) and fires the hidden Close past the threshold.
  const isBottomDrawer = drawer === "md" || drawer === true || drawer === "sm";
  const drawerHideAt = drawer === "sm" ? "sm:hidden" : "md:hidden";
  // Close-X visibility. `showCloseButton` (default true) governs whether the corner X
  // renders. A bottom drawer now shows it on BOTH tiers by default — the grabber AND a
  // native-app-style corner X on mobile — so mobile popups get a close affordance without
  // opting in per-page. `showCloseButton={false}` still fully hides it (the grabber-only
  // drawers keep no X); pair it with `mobileCloseButton` to force a mobile-only X.
  // Inside the mobile app's card webview (PRD 12 D13), match the app's own BottomSheet
  // look rather than the web dialog: a blurred + lightly-dimmed backdrop (vs the site's
  // solid bg-black/50) and NO corner close X on the bottom drawer — the app's sheets
  // dismiss by grabber-drag or backdrop-tap, both of which still work here.
  const embedded = isEmbeddedInApp();
  // A non-dismissible dialog has neither of those, so it keeps its X even in the app.
  const dismissible = React.useContext(DialogDismissibleContext);
  const renderClose =
    (showCloseButton || (mobileCloseButton && isBottomDrawer)) &&
    !(embedded && isBottomDrawer && dismissible);
  const closeVisibility =
    !isBottomDrawer || showCloseButton
      ? "" // centered dialogs + bottom drawers both show the X on every tier
      : drawer === "sm"
        ? "sm:hidden" // showCloseButton=false + mobileCloseButton → mobile-only X
        : "md:hidden";
  const closeRef = React.useRef<HTMLButtonElement>(null);
  // On touch the whole panel drags (yielding to a scrollable that isn't at its top);
  // a mouse keeps the grabber, so desktop text selection is unaffected.
  const { panelProps, grabberProps } = useDrawerDrag(closeRef);
  // A non-dismissible dialog keeps its drawer look but not the drag-to-dismiss gesture.
  const draggable = isBottomDrawer && dismissible;
  // While docked to the bottom, sit on top of the on-screen keyboard and shrink to the
  // space above it (the native sheets' behaviour) — see virtual-keyboard.ts.
  const docked = useMediaQuery(drawer === "sm" ? "(max-width: 766px)" : "(max-width: 768px)");
  const keyboardInset = useVirtualKeyboardInset(isBottomDrawer && docked);

  return (
    <DialogPortal>
      <DialogOverlay
        className={cn(
          scopeClass,
          embedded
            ? // App-parity: blur + the BottomSheet's light rgba(0,0,0,0.1) dim.
              "bg-black/10 backdrop-blur-md"
            : !isAdmin && "bg-black/50 [backdrop-filter:none]",
          // A docked drawer's backdrop never pans: with the keyboard up, iOS Safari would
          // otherwise scroll the page behind under a swipe that starts on it (BDAY-279).
          isBottomDrawer && (drawer === "sm" ? "max-sm:touch-none" : "max-md:touch-none"),
          overlayClassName,
        )}
      />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        aria-modal="true"
        className={cn(
          scopeClass,
          // Old app dialogs used MUI <Slide direction="up"> (225ms in / 195ms out,
          // cubic-bezier(0.4,0,0.2,1)) — a slide-up-appear, not a zoom "pop". Match that
          // feel here: fade + slide-up on the centered variants (per-variant below), at
          // MUI's standard easing/duration. Base translate stays on the CSS `translate:`
          // property (Tailwind v4), so it composes with the animated `transform:` and the
          // dialog stays centered while it slides.
          "fixed z-50 grid grid-cols-1 gap-4 overflow-hidden overscroll-y-contain border bg-background p-5 shadow-lg outline-none duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
          drawer === "md" || drawer === true
            ? // Bottom sheet below md (769px — the mobile tier); centered at md+.
              // `true` (bare `mobileDrawer`) is an alias for this default breakpoint.
              "max-md:inset-x-0 max-md:bottom-0 max-md:top-auto max-md:w-full max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-t-2xl max-md:rounded-b-none max-md:will-change-transform max-md:data-open:slide-in-from-bottom-full max-md:data-closed:slide-out-to-bottom-full md:top-1/2 md:left-1/2 md:w-[calc(100%-2rem)] md:max-w-lg md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-2xl md:data-open:slide-in-from-bottom-8 md:data-closed:slide-out-to-bottom-8"
            : drawer === "sm"
              ? // Explicit opt-in: bottom sheet only below sm (767px); centered at sm+.
                "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-t-2xl max-sm:rounded-b-none max-sm:will-change-transform max-sm:data-open:slide-in-from-bottom-full max-sm:data-closed:slide-out-to-bottom-full sm:top-1/2 sm:left-1/2 sm:w-[calc(100%-2rem)] sm:max-w-lg sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:data-open:slide-in-from-bottom-8 sm:data-closed:slide-out-to-bottom-8"
              : // Centered at every width — mobile full-bleed is opted into per-dialog.
                "top-1/2 left-1/2 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-2xl data-open:slide-in-from-bottom-8 data-closed:slide-out-to-bottom-8",
          className,
        )}
        {...props}
        style={{ ...props.style, ...keyboardDrawerStyle(keyboardInset) }}
        {...(draggable ? panelProps : {})}
      >
        {draggable && (
          <>
            {/* Absolutely pinned to the very top edge so the grabber ALWAYS hugs the
                top regardless of the popup's own top padding (a margin pull-up sat too
                low whenever a dialog added extra pt — this makes "grabber at the top"
                the default for every mobile drawer). */}
            <DrawerGrabber
              grabberProps={grabberProps}
              className={cn("absolute inset-x-0 top-0", drawerHideAt)}
            />
            <DialogPrimitive.Close
              render={<button type="button" ref={closeRef} className="hidden" tabIndex={-1} />}
            />
          </>
        )}
        {children}
        {renderClose && (
          <div className={cn("absolute top-3 right-4 z-10", closeVisibility)}>
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={<Button variant="ghost" size={largeCloseButton ? "icon-lg" : "icon-sm"} />}
            >
              <XIcon
                strokeWidth={largeCloseButton ? 3 : 2}
                className={cn(largeCloseButton ? "size-7" : "size-5")}
              />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="dialog-header" className={cn("flex flex-col gap-2", className)} {...props} />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("leading-none font-medium", className)}
      {...props}
    />
  );
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-base text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
